import { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Icon } from '../design-system/icons';
import { ResultImageOverlay } from '../components/onboarding/ResultImageOverlay';
import { useIsDesktop } from '../components/onboarding/useIsDesktop';
import { useLocation } from 'react-router-dom';
import { useStepFade } from '../components/onboarding/useStepFade';
import { SetupPricingSheet } from '../components/pricing/SetupPricingSheet';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  GENERATION_RESULTS,
  SETUP_DOWNLOADED_KEY,
  SETUP_GENERATION_KEY,
  type ResultImage,
} from '../components/onboarding/accountSetup';
import { useUserGroup } from '../hooks/useUserGroup';
import { landingIsV2 } from '../lib/userGroups';
import { descargarArchivo } from '../utils/descargarArchivo';
import { buildDownloadFileName } from '../utils/downloadFileName';
import { marcarDescargaDelOnboarding } from '../lib/onboardingTracking';

/**
 * Pantalla de resultados de la generación (onboarding).
 *
 * **Dos layouts por dispositivo (pedido del usuario):**
 * - **Mobile (`< lg`):** la pantalla **previa** que ya estaba bien — grilla 2×2 de
 *   resultados; tocar una abre la vista ampliada; promo de créditos +
 *   CTA "Seguir explorando Atlas". Es una pantalla **propia** (full-screen, sin shell).
 * - **Desktop (`≥ lg`):** vuelve al layout del onboarding **clásico** (preview grande
 *   a la izquierda + panel con título, selector de imagen — cards estilo `SelectableCard`
 *   del onboarding — y los botones "Descarga imagen" + "Seguir explorando Atlas"), y se
 *   renderiza **dentro del shell de la app** (sidebar). El
 *   wrapping en `DashboardLayout` — sólo desktop — vive en la ruta de `App.tsx`.
 *
 * Ambos comparten datos y conservan las **mejoras**: título, **precarga full-res** y la
 * promo de créditos.
 *
 * Las miniaturas llevan texto debajo **sólo con los resultados de ejemplo**
 * (ADR-2026-08-04-label-solo-en-los-resultados-de-ejemplo): ahí el label dice algo
 * ("Foto catálogo", "Video"), mientras que en la generación real es "Opción N" y sólo
 * hace ruido. El `label` existe en los dos casos igual, porque además de mostrarse es la
 * etiqueta accesible (`aria-label`, `alt`) y el nombre del archivo al descargar.
 *
 * Los resultados dependen del ejemplo preseleccionado en el paso 4 (`localStorage`):
 * "Cartera verde" o "Camisa de hombre" (fallback `cartera_verde`).
 */
/** Duración (ms) de cada lado del fade (out y luego in) de la preview desktop al cambiar de imagen. */
const PREVIEW_FADE_MS = 220;

/**
 * Botón de play centrado sobre el póster de un resultado de tipo "Video".
 * Replica el badge de Figma (node 195:195): círculo `rgba(0,0,0,0.8)` con
 * padding de 12px y el ícono `solid/play` del DS (20px, blanco). El `size` es
 * el **diámetro** del círculo; el ícono escala proporcional (20/44 en el diseño).
 */
function PlayBadge({ size = 44 }: { size?: number }) {
  const iconSize = Math.round(size * (20 / 44));
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span
        className="flex items-center justify-center rounded-full bg-black/80"
        style={{ width: size, height: size }}
      >
        <Icon name="solid/play" size={iconSize} className="text-white" />
      </span>
    </span>
  );
}

export interface AccountSetupResultsProps {
  /**
   * Preview de desarrollo (ADR-2026-07-31-pantalla-resultados-alta-upsell-hacer). Sólo lo pasa la ruta `/onboarding-pricing-dev`,
   * que está detrás de `import.meta.env.DEV`, así que en producción llega siempre
   * `false` y la pantalla se comporta exactamente como antes.
   *
   * Hace dos cosas, las dos para poder mirar el upsell de planes sin reproducir el
   * alta entera:
   * - **No rebota a `/bienvenida` sin sesión.** Se puede porque esta pantalla no
   *   pide datos: los resultados son estáticos (`GENERATION_RESULTS`). Es la razón
   *   por la que ADR-2026-07-30-bypass-dev-onboarding-entrar-app no tocó este guard en las otras pantallas — allá sí hacen
   *   falta datos de Supabase.
   * - **Arranca como si ya hubiera descargado**, que es la condición del upsell
   *   (ADR-2026-07-29-upsell-planes-salir-alta-condicionado). Sin esto, "Seguir explorando Atlas" sigue derecho al alta.
   */
  devPreview?: boolean;
}

export const AccountSetupResults = ({ devPreview = false }: AccountSetupResultsProps = {}) => {
  const isDesktop = useIsDesktop();
  const { visible, navigate, navigateWithFade } = useStepFade();

  /**
   * Salida del onboarding por grupo, calculada ACÁ y no vía `/` (HomeRedirect):
   * ese redirect decide con el cache de useUserGroup, y un usuario recién creado
   * puede tener el grupo todavía en null (lo asigna el worker post-setup, async) —
   * caía al flujo clásico aunque fuera Grupo B. Montar el hook en ESTA pantalla
   * arranca el fetch mientras el usuario mira sus imágenes: al tocar "Seguir" el
   * grupo real ya está resuelto y CACHEADO (de paso, HomeRedirect también queda
   * bien para futuras entradas). Mismo criterio de destino que ADR-833.
   * En ambos destinos el prompt "Instalar como app / Abrir en navegador" está
   * montado y auto-gateado — aparece solo a los ~2s de aterrizar.
   */
  const { isDevTester, grupo } = useUserGroup();
  const salidaOnboarding = () =>
    landingIsV2(isDevTester, grupo)
      ? '/interfaz-v2'
      : '/redes-sociales/crear-anuncio/seleccionar-productos';
  // `state.resultadosReales`: las imágenes generadas de verdad que manda la pantalla
  // de generación cuando el usuario subió su propia foto.
  const location = useLocation();
  const [results, setResults] = useState<ResultImage[]>(GENERATION_RESULTS.cartera_verde);
  /**
   * ¿Estos resultados salieron de una generación REAL (el usuario subió su foto) o son
   * el set de demo? Decide si las miniaturas llevan texto debajo
   * (ADR-2026-08-04-label-solo-en-los-resultados-de-ejemplo): los de ejemplo tienen un
   * nombre que dice algo ("Foto catálogo", "Video") y los reales sólo "Opción N".
   *
   * Se resuelve por ORIGEN y no mirando el label, porque el label sigue existiendo en
   * los dos casos — se usa igual para el `alt`, el `aria-label` y el nombre del archivo
   * al descargar.
   */
  const [sonGeneradas, setSonGeneradas] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [preview, setPreview] = useState<ResultImage | null>(null);
  // Preview desktop con crossfade: `displayed` es lo que se muestra (va detrás de
  // `selected`); al cambiar la selección se hace fade-out, se swapea y fade-in.
  const [displayed, setDisplayed] = useState<ResultImage | null>(null);
  const [previewVisible, setPreviewVisible] = useState(true);
  // ¿Ya descargó alguna imagen/video? Es la condición del upsell de planes
  // (ADR-2026-07-29-upsell-planes-salir-alta-condicionado). Se persiste para que un refresh no la pierda.
  const [downloaded, setDownloaded] = useState(devPreview);
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    let active = true;
    if (!devPreview) {
      supabase.auth.getSession().then(({ data }) => {
        if (active && !data.session)
          navigate(ONBOARDING_V2.bienvenida, { replace: true });
      });
    }
    let id: string | null = null;
    try {
      id = localStorage.getItem(SETUP_GENERATION_KEY);
      if (localStorage.getItem(SETUP_DOWNLOADED_KEY) === '1') setDownloaded(true);
    } catch {}
    // Generación REAL (el usuario subió su foto): las imágenes vienen ya precargadas
    // desde la pantalla de generación, con sus URLs firmadas
    // (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real). Tienen prioridad sobre el set
    // de demo; si no hay (ejemplo precargado, o refresh que perdió el state) se usa el
    // set del ejemplo elegido.
    const reales = (location.state as { resultadosReales?: ResultImage[] } | null)
      ?.resultadosReales;
    if (reales?.length) {
      setResults(reales);
      setSonGeneradas(true);
      setSelectedIdx(0);
    } else if (id && GENERATION_RESULTS[id]) {
      setResults(GENERATION_RESULTS[id]);
      setSonGeneradas(false);
      setSelectedIdx(0);
    }
    return () => {
      active = false;
    };
  }, [navigate, devPreview, location.state]);

  // Precarga por detrás las versiones ORIGINALES full-res (`full`) para que el
  // preview grande / la vista ampliada las muestren al instante (mejora conservada).
  useEffect(() => {
    const preloaders = results
      .filter((img) => img.full && img.full !== img.src)
      .map((img) => {
        const pre = new Image();
        pre.src = img.full!;
        return pre;
      });
    return () => {
      preloaders.forEach((pre) => {
        pre.src = '';
      });
    };
  }, [results]);

  const selected = results[selectedIdx] ?? results[0];

  // Crossfade de la preview (desktop): la primera vez fija `displayed` sin animar;
  // luego, al cambiar `selected`, hace fade-out (opacidad 0), swapea el contenido y
  // fade-in. `PREVIEW_FADE_MS` por lado → transición sutil y relativamente lenta.
  useEffect(() => {
    if (displayed === null) {
      setDisplayed(selected);
      return;
    }
    if (selected === displayed) return;
    setPreviewVisible(false);
    const t = setTimeout(() => {
      setDisplayed(selected);
      setPreviewVisible(true);
    }, PREVIEW_FADE_MS);
    return () => clearTimeout(t);
  }, [selected, displayed]);

  /**
   * Marca que el usuario descargó (cualquier resultado, en cualquiera de los dos layouts).
   *
   * Va a **dos lados**: al `localStorage`, que es lo que decide el upsell de planes y por
   * eso tiene que sobrevivir a un refresh, y a la empresa en Supabase, para poder medirlo
   * desde afuera (ADR-2026-08-07-onboarding-producto-y-descarga-en-supabase).
   *
   * El update a Supabase sale **una vez por visita a la pantalla**: bajarse las cuatro
   * imágenes no son cuatro updates, y a la vez un refresh vuelve a intentarlo si la
   * primera vez no llegó.
   */
  const marcadoEnSupabase = useRef(false);
  const markDownloaded = () => {
    setDownloaded(true);
    try {
      localStorage.setItem(SETUP_DOWNLOADED_KEY, '1');
    } catch {}
    if (!marcadoEnSupabase.current) {
      marcadoEnSupabase.current = true;
      marcarDescargaDelOnboarding();
    }
  };

  /**
   * "Seguir explorando Atlas". Si el usuario ya se llevó una imagen, antes de
   * seguir al alta ve el catálogo de planes (bottom sheet en mobile, overlay en
   * desktop). Si no descargó nada, sigue derecho como siempre. Ver ADR-2026-07-29-upsell-planes-salir-alta-condicionado.
   */
  const handleContinue = () => {
    if (downloaded) setShowPricing(true);
    // Fin del onboarding: a la home del GRUPO (no a '/', ver salidaOnboarding).
    // Antes acá venía el alta clásica (`/onboarding`), que ya no existe — la
    // empresa se creó al terminar el paso 3 (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
    else navigateWithFade(salidaOnboarding());
  };

  /**
   * El back del navegador (o el gesto de "volver" del celular, el edge-swipe) **no puede
   * devolver al paso de generar**: el alta ya terminó, la empresa está creada y el paso 4
   * volvería a ofrecer generar (el jefe lo reprodujo justo con el edge-swipe del celular).
   * Acá el gesto **no hace nada** — el usuario sale por el botón "Seguir explorando Atlas".
   *
   * Cómo: se apila una entrada propia al montar y, cada vez que el back la consume, se
   * vuelve a apilar → el gesto queda neutralizado SOLO en esta pantalla (el efecto se
   * limpia al desmontar; el resto de la web conserva el gesto). Combinado con llegar acá
   * con `replace` (el paso de generar ni queda en el historial). Y si un pop MÚLTIPLE (un
   * swipe muy largo que se come varias entradas de una) llegara a sacarnos igual, se
   * detecta y se vuelve a resultados por router antes de re-armar → nunca se queda trabado
   * en el cuestionario (desde donde se podría regenerar). Re-apilar `window.location.href`
   * suelto no alcanzaba: tras un pop múltiple es la URL ya popeada, no la de resultados.
   * Greptile PR#70.
   *
   * En la preview de dev NO se traba: es una pantalla para mirar y trabar el back molesta.
   * Supersede ADR-2026-08-03-back-en-resultados-sale-como-el-boton (que hacía back = botón,
   * pero no aguantaba el edge-swipe de iOS). Ver ADR-2026-08-14-onboarding-back-no-regenera.
   */
  useEffect(() => {
    if (devPreview) return;
    const onPop = () => {
      // Si un pop múltiple nos sacó de resultados, volvemos por router (re-render correcto)
      // ANTES de re-armar; si seguimos acá (back normal), sólo re-armamos → no hace nada.
      if (window.location.pathname !== ONBOARDING_V2.setupResultados) {
        navigate(ONBOARDING_V2.setupResultados, { replace: true });
      }
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [devPreview, navigate]);

  /**
   * Salida del sheet (cerrar / arrastrar / omitir / suscribirse): continúa el
   * flujo. Llega ya con la animación de salida terminada, así que desmontarlo
   * acá no corta nada.
   */
  const handlePricingDismiss = () => {
    setShowPricing(false);
    navigateWithFade(salidaOnboarding());
  };

  const pricingSheet = showPricing ? (
    <SetupPricingSheet onDismiss={handlePricingDismiss} />
  ) : null;

  // ─────────────────────────────── MOBILE (pantalla previa) ───────────────────────────────
  if (!isDesktop) {
    return (
      <div
        // `pwa-screen` (alto medido por JS) en vez del `min-h-[100svh]` que tenía: era
        // la ÚNICA pantalla del onboarding que usaba unidades de viewport CSS a mano, y
        // `svh` queda corto en el acceso directo instalado — el mismo motivo por el que
        // existe `--app-height` (ADR-652). Ahora comparte mecanismo con el resto.
        className={`pwa-screen flex flex-col bg-white font-sans px-5 pt-[calc(env(safe-area-inset-top)+20px)] pb-[calc(env(safe-area-inset-bottom)+40px)] transition-opacity duration-[250ms] ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex w-full flex-1 flex-col gap-8">
          <h1 className="text-ds-heading-xl text-[#18181B]">Disfrutá de tus primeras fotos profesionales</h1>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {results.map((img) => (
              <button
                key={img.label}
                type="button"
                onClick={() => setPreview(img)}
                className="flex flex-col items-center gap-3 outline-none focus-visible:opacity-80"
                aria-label={`Ver ${img.label} en pantalla completa`}
              >
                <div className="relative w-full overflow-hidden rounded-[16px] border border-[#E4E4E7] transition-opacity duration-200 hover:opacity-90">
                  <img
                    src={img.src}
                    alt={img.label}
                    draggable={false}
                    className="aspect-[173/216] w-full object-cover"
                  />
                  {img.isVideo && <PlayBadge />}
                </div>
                {!sonGeneradas && (
                  <span className="text-[14px] font-[600] leading-[19px] text-[#52525C]">{img.label}</span>
                )}
              </button>
            ))}
          </div>

          {/* Promo + CTA final, pegado abajo. */}
          <div className="mt-auto flex flex-col items-center">
            <div className="z-0 -mb-2.5 flex w-full items-center justify-center gap-2 rounded-t-[12px] border border-[#E4E4E7] bg-[#FAFAFA] px-4 pb-[18px] pt-1.5">
              <Icon name="solid/sparkles" size={14} className="text-[#18181B]" />
              <span className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
                Tenés 10 créditos gratuitos esperándote
              </span>
            </div>
            <button
              type="button"
              onClick={handleContinue}
              className="relative z-10 flex w-full items-center justify-center rounded-[12px] bg-[linear-gradient(181.87deg,#2563F5_8.76%,#0436AE_90.56%)] p-4 text-[16px] font-[700] leading-[21px] text-[#FAFAFA] transition-[filter] duration-200 hover:brightness-110"
            >
              Seguir explorando Atlas
            </button>
          </div>
        </div>

        {preview && (
          <ResultImageOverlay
            image={preview}
            onClose={() => setPreview(null)}
            onDownload={markDownloaded}
          />
        )}
        {pricingSheet}
      </div>
    );
  }

  // ─────────────────────────────── DESKTOP (layout clásico, dentro del shell) ───────────────────────────────
  // Mismo mecanismo que el resto de la app (`utils/descargarArchivo`): blob local en vez
  // del `<a download>` directo a la URL remota, que los navegadores IGNORAN cuando es
  // cross-origin — con las imágenes reales (Supabase Storage) el archivo terminaba
  // abriéndose en vez de bajarse, y con el nombre equivocado.
  // Ver ADR-2026-08-03-descarga-del-onboarding-usa-el-mecanismo-de-la-app.
  const handleDownload = async () => {
    if (!selected) return;
    const esVideo = !!(selected.isVideo && selected.video);
    try {
      const ok = await descargarArchivo({
        url: esVideo ? selected.video! : (selected.full ?? selected.src),
        fileName: buildDownloadFileName({ variantName: selected.label, forceVideo: esVideo }),
        esVideo,
      });
      // `markDownloaded` habilita el upsell al salir del alta: sólo si bajó de verdad.
      if (ok) markDownloaded();
    } catch {
      /* el botón queda disponible para reintentar */
    }
  };

  return (
    <div
      className={`flex h-full min-h-full flex-col bg-white font-sans transition-opacity duration-[250ms] ease-out ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Preview (izq) + panel (der), llena el alto. */}
      <div className="flex min-h-0 flex-1 flex-row gap-6 p-8">
        {/* Preview de la seleccionada: respeta el ASPECT RATIO de la imagen (sin recorte),
            lo más grande posible (llena el alto disponible) y centrada. Sin click en desktop.
            Muestra `displayed` (no `selected`) con un fade-out/fade-in al cambiar de imagen/video. */}
        <div
          className={`flex min-h-0 flex-1 items-center justify-center transition-opacity ease-in-out ${
            previewVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDuration: `${PREVIEW_FADE_MS}ms` }}
        >
          {displayed?.isVideo && displayed.video ? (
            <video
              key={displayed.video}
              src={displayed.video}
              poster={displayed.src}
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-auto max-w-full rounded-[16px] object-contain"
            />
          ) : (
            <img
              src={displayed?.full ?? displayed?.src}
              alt={displayed?.label}
              draggable={false}
              className="h-full w-auto max-w-full rounded-[16px] object-contain"
            />
          )}
        </div>

        {/* Panel derecho: ocupa el 50% del ancho (pedido del usuario). Columna en flex con la
            grilla scrolleable en el medio para que los botones de abajo queden SIEMPRE visibles. */}
        <div className="flex w-1/2 min-h-0 shrink-0 flex-col gap-5">
          {/* Título (texto plano, sin pill). */}
          <h1 className="shrink-0 text-[20px] font-[700] leading-[26px] text-[#18181B]">
            Disfrutá de tus primeras fotos profesionales de producto
          </h1>

          {/* Selector de imagen (pedido del usuario): grilla de 3 columnas (3 por fila), imagen
              portrait de borde a borde, con label debajo sólo si son resultados de ejemplo.
              La selección es un **borde negro sobre
              la imagen** — `ring` outset sin offset (un `ring-inset` quedaría tapado por el `<img>`
              que llena el contenedor). El contenedor scrollea (`min-h-0 overflow-y-auto`) para que
              los botones de abajo no se tapen; el `p-0.5` evita que el ring se recorte al scrollear. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div role="radiogroup" aria-label="Elegí una imagen" className="grid grid-cols-3 gap-x-4 gap-y-4 p-0.5">
              {results.map((img, i) => {
                const isSel = i === selectedIdx;
                return (
                  <button
                    key={img.label}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    onClick={() => setSelectedIdx(i)}
                    aria-label={`Elegir ${img.label}`}
                    className="flex flex-col items-center gap-3 outline-none focus-visible:opacity-80"
                  >
                    <div
                      className={`relative w-full overflow-hidden rounded-[16px] transition-shadow duration-200 ${
                        isSel
                          ? 'ring-2 ring-[#18181B]'
                          : 'ring-1 ring-[#E4E4E7] hover:ring-[#9F9FA9]'
                      }`}
                    >
                      <img src={img.src} alt="" draggable={false} className="aspect-[4/5] w-full object-cover" />
                      {img.isVideo && <PlayBadge />}
                      {/* Borde blanco INTERIOR al seleccionar: overlay transparente encima de la
                          imagen con `ring-inset` blanco (un ring-inset en el contenedor lo taparía
                          el `<img>`). Junto al borde negro exterior forma un doble borde que hace
                          visible la selección aun sobre imágenes con bordes oscuros. */}
                      {isSel && (
                        <span className="pointer-events-none absolute inset-0 rounded-[16px] ring-2 ring-inset ring-white" />
                      )}
                    </div>
                    {!sonGeneradas && (
                      <span className="text-[14px] font-[600] leading-[19px] text-[#52525C]">{img.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Promo de créditos (mismo look que mobile: fondo claro `#FAFAFA` + stroke) + botones abajo.
              `shrink-0` para que queden SIEMPRE visibles (la grilla de arriba scrollea). */}
          <div className="flex shrink-0 flex-col gap-3 pt-1">
            <div className="flex items-center justify-center gap-2 rounded-[12px] border border-[#E4E4E7] bg-[#FAFAFA] px-4 py-2.5">
              <Icon name="solid/sparkles" size={14} className="shrink-0 text-[#18181B]" />
              <span className="text-[13px] font-[600] leading-[18px] text-[#18181B]">
                Tenés 10 créditos gratuitos esperándote
              </span>
            </div>
            <div className="flex gap-3">
              {/* Descargar: negro, texto + ícono en blanco. */}
              <button
                type="button"
                onClick={handleDownload}
                className="flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-[#18181B] p-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A]"
              >
                <Download size={20} />
                Descarga imagen
              </button>
              {/* Seguir explorando Atlas: fondo en degradé (el del onboarding). */}
              <button
                type="button"
                onClick={handleContinue}
                className="flex flex-1 items-center justify-center rounded-[12px] bg-[linear-gradient(181.87deg,#2563F5_8.76%,#0436AE_90.56%)] p-4 text-[15px] font-[700] leading-[19px] text-white transition-[filter] duration-200 hover:brightness-110"
              >
                Seguir explorando Atlas
              </button>
            </div>
          </div>
        </div>
      </div>

      {pricingSheet}
    </div>
  );
};

export default AccountSetupResults;
